import React, { useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';
import numeral from 'numeral';
import { parseIsoDateOrTimestamp } from '@wix/da-i18next/pkg/time/functions';

export interface Props {
  /** either a ISO8601 string or unix timestamp */
  countToDate: string;
  /** Render at minute updates */
  minutesOnly?: boolean;
  /** Render function for custom display */
  renderTimer?: (days, hours, minutes, seconds) => React.ReactNode;
  /** callback called when the countdown ends */
  onEnd?: () => void;
  /** An optional classname for the wrapper element */
  className?: string;
  /** An optional classname for the timer value element */
  timerValueClassName?: string;
  /** An optional classname for the wrapper element, to be applied when the countdown has finished */
  endedClassName?: string;
  /** message to show when timer ends */
  endedMessage?: string;
  /** message to show before the countdown */
  prefix?: string;
}

function countdownClock(
  { remainingTime, setRemaining, handlerRef, startRef, lastUpdateRef, minutesOnly, onEnd },
  timestamp
) {
  if (startRef.current === undefined) {
    startRef.current = timestamp + remainingTime;
  }
  if (!lastUpdateRef.current) {
    lastUpdateRef.current = timestamp;
  }

  const renderWindow = minutesOnly ? 60000 : 1000;

  if (timestamp - lastUpdateRef.current < renderWindow) {
    handlerRef.current = requestAnimationFrame(
      countdownClock.bind(null, {
        remainingTime,
        setRemaining,
        handlerRef,
        startRef,
        lastUpdateRef,
        minutesOnly,
        onEnd,
      })
    );
    return;
  }

  const currentTime = startRef.current - timestamp;
  if (currentTime <= 0) {
    cancelAnimationFrame(handlerRef.current);
    setRemaining(0);
    onEnd && onEnd();
    return;
  }

  setRemaining(currentTime);
  lastUpdateRef.current = timestamp;

  // Loop
  handlerRef.current = requestAnimationFrame(
    countdownClock.bind(null, {
      remainingTime,
      setRemaining,
      handlerRef,
      startRef,
      lastUpdateRef,
      minutesOnly,
      onEnd,
    })
  );
  return currentTime;
}

function getDiff(remaining: number) {
  if (remaining > 0) {
    return {
      days: Math.floor(remaining / (1000 * 86400)),
      hours: Math.floor((remaining / (1000 * 3600)) % 24),
      minutes: Math.floor((remaining / (1000 * 60)) % 60),
      seconds: Math.floor((remaining / 1000) % 60),
    };
  }
  return {
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0,
  };
}

export const Countdown: React.FC<Props> = ({
  countToDate,
  minutesOnly,
  className,
  timerValueClassName,
  endedClassName,
  renderTimer,
  onEnd,
  endedMessage,
  prefix,
}) => {
  const handlerRef = useRef<number | undefined>(undefined);
  const startRef = useRef<number | undefined>(undefined);
  const lastUpdateRef = useRef<number | undefined>(undefined);

  // Calc the time in ms between the date and now
  const remainingTime = useMemo(() => {
    const toDate = parseIsoDateOrTimestamp(countToDate);
    return toDate.getTime() - Date.now();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [remaining, setRemaining] = useState<any>(null);

  useEffect(() => {
    handlerRef.current = requestAnimationFrame(
      countdownClock.bind(null, {
        remainingTime,
        setRemaining,
        handlerRef,
        startRef,
        lastUpdateRef,
        minutesOnly,
        onEnd,
      })
    );
    return () => {
      if (handlerRef.current) {
        cancelAnimationFrame(handlerRef.current);
      }
    };
    // This is animation loop so we never want to double bind it
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const diff = getDiff(remaining === null ? remainingTime : remaining);
  const timerEnded = (remaining === null ? remainingTime : remaining) < 1;

  // render the time unless countdown is over and we have a custom message to show ("Offer locked")
  if (renderTimer && !(timerEnded && endedMessage)) {
    return (
      <span className={cn(className, timerEnded && endedClassName)}>
        {prefix} {renderTimer(diff.days, diff.hours, diff.minutes, diff.seconds)}
      </span>
    );
  }

  const timer = minutesOnly ? (
    <>
      {diff.days > 0 ? `${numeral(diff.days).format('00')}:` : ''}
      {numeral(diff.hours).format('00')}:{numeral(diff.minutes).format('00')}
    </>
  ) : (
    <>
      {diff.days > 0 ? `${numeral(diff.days).format('00')}:` : ''}
      {numeral(diff.hours).format('00')}:{numeral(diff.minutes).format('00')}:
      {numeral(diff.seconds).format('00')}
    </>
  );

  return (
    <div className={cn(className, timerEnded && endedClassName)}>
      {!timerEnded && prefix}{' '}
      {timerEnded && endedMessage ? (
        endedMessage
      ) : (
        <span className={timerValueClassName}>{timer}</span>
      )}
    </div>
  );
};
Countdown.displayName = 'Countdown';

export default Countdown;
